import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Button
} from 'reactstrap';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/autoplay';
import image1 from '../assets/images/swiper1.avif';
import image2 from '../assets/images/swiper2.avif';
import image3 from '../assets/images/swiper3.avif';
import image6 from '../assets/images/swiper6.avif';
import image7 from '../assets/images/swiper7.avif';
import image8 from '../assets/images/swiper8.avif';
import image9 from '../assets/images/swiper9.avif'; 
import axios from 'axios';
import api from '../../config.js';

const Banner = () => {
  const [bannerData, setBannerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fallback images
  const fallbackDownwardImages = [image1, image2, image3, image8, image3];
  const fallbackUpwardImages = [image6, image7, image8, image9, image1];
  
  const fetchBannerData = async () => {
    try {
      setLoading(true);
       const response = await axios.get('/api/public/banners');      
      if (response.data.isOk) {
        const banner = response.data.data[0]; // Get first banner
        
        // Process images with full URLs
        const processedBanner = {
          title: banner.title || "Home to the industry's most influential leaders.",
          subtitle: banner.subtitle || "Bringing together the C-suite decision-makers defining cybersecurity and technology at the highest level.",
          leftColumn: banner.leftColumn?.map(imagePath =>`${api.API_URL}/${imagePath}`),
          rightColumn: banner.rightColumn?.map(imagePath => `${api.API_URL}/${imagePath}`)
        };
        
        setBannerData(processedBanner);
      } else {
        console.warn('No banner data found, using fallback');
        setBannerData({
          title: "Home to the industry's most influential leaders.",
          subtitle: "Bringing together the C-suite decision-makers defining cybersecurity and technology at the highest level.",
          leftColumn: fallbackDownwardImages,
          rightColumn: fallbackUpwardImages
        });
      }
    } catch (error) {
      console.error('Error fetching banner data:', error);
      setError(error.message);
      // Use fallback data on error
      setBannerData({
        title: "Home to the industry's most influential leaders.",
        subtitle: "Bringing together the C-suite decision-makers defining cybersecurity and technology at the highest level.",
        leftColumn: fallbackDownwardImages,
        rightColumn: fallbackUpwardImages
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBannerData();
  }, []);

  // Get current data or fallback
  const currentData = bannerData || {
    title: "Home to the industry's most influential leaders.",
    subtitle: "Bringing together the C-suite decision-makers defining cybersecurity and technology at the highest level.",
    leftColumn: fallbackDownwardImages,
    rightColumn: fallbackUpwardImages
  };
  
  // Duplicate images for seamless loop
  const duplicatedDownwardImages = [...currentData.leftColumn, ...currentData.leftColumn];
  const duplicatedUpwardImages = [...currentData.rightColumn, ...currentData.rightColumn];
  
  return (
    <>
      <section className="hero-section">
        <Container>
          <Row className="align-items-center">
            
            <Col lg={5} className="mb-4 mb-lg-0 banner-custom">
              {loading ? (
                <div className="d-flex align-items-center">
                  <div className="spinner-border text-primary me-3" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span>Loading banner...</span>
                </div>
              ) : (
                <>
                  <h1 className="hero-heading">
                    {currentData.title}
                  </h1>
                  <p className="hero-subtext">
                    {currentData.subtitle}
                  </p>
                  {error && (
                    <div className="alert alert-warning mt-2">
                      <small><i className="fas fa-exclamation-triangle me-1"></i>
                      Using default content due to API error</small>
                    </div>
                  )}
                </>
              )}
              <div className="d-flex gap-2">
                <Button 
                  tag="a" 
                  href="/upcoming-events" 
                  className="btn-red"
                >
                  Upcoming Events
                </Button>
                <Button 
                  tag="a" 
                  href="/partner-with-us" 
                  color="dark"
                >
                  Partner With Us
                </Button>
              </div>
            </Col>

            <Col lg={7}>
              <div className="d-flex gap-3 vertical-stack">

                {/* Downward Slider */}
                <Swiper
                  direction="vertical"
                  slidesPerView={3}
                  spaceBetween={20}
                  speed={5000}
                  autoplay={{
                    delay: 0,
                    disableOnInteraction: false,
                    reverseDirection: false,
                  }}
                  loop={true}
                  modules={[Autoplay]}
                  className="swiper swiper-down"
                  allowTouchMove={false}
                >
                  {duplicatedDownwardImages.map((imgSrc, index) => (
                    <SwiperSlide key={`downward-${index}`}>
                      <img 
                        src={imgSrc} 
                        className="img-fluid rounded-3" 
                        alt={`Banner Image ${index + 1}`}
                        loading="lazy"
                        onError={(e) => {
                          console.warn(`Failed to load image: ${imgSrc}`);
                        }}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>

                {/* Upward Slider */}
                <Swiper
                  direction="vertical"
                  slidesPerView={3}
                  spaceBetween={20}
                  speed={5000}
                  autoplay={{
                    delay: 0,
                    disableOnInteraction: false,
                    reverseDirection: true,
                  }}
                  loop={true}
                  modules={[Autoplay]}
                  className="swiper swiper-down"
                  allowTouchMove={false}
                >
                  {duplicatedUpwardImages.map((imgSrc, index) => (
                    <SwiperSlide key={`upward-${index}`}>
                      <img 
                        src={imgSrc} 
                        className="img-fluid rounded-3" 
                        alt={`Banner Image ${index + 1}`}
                        loading="lazy"
                        onError={(e) => {
                          console.warn(`Failed to load image: ${imgSrc}`);
                        }}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>

              </div>
            </Col>

          </Row>
        </Container>
      </section>

      {/* Content Section */}
      <section className="bg-light-pink text-center">
        <Container>
          <p className="content-section">
            ApexCXOs is an international leadership forum and community that brings
            together CISOs, CIOs, CTOs, and CDOs from diverse backgrounds under a singular
            purpose: to{' '}
            <span className="text-red">drive change</span>,{' '}
            <span className="text-red">create impact</span>, and{' '}
            <span className="text-red">lead at the forefront of technology</span>.
          </p>
        </Container>
      </section>
    </>
  );
};

export default Banner;
